import { createSelector } from '@reduxjs/toolkit';
import {
  filter, map, isEmpty, orderBy,
  uniqBy,
} from 'lodash';
import { pickPrimitives } from '@sda/utils/type';
import { getDateTime } from '@sda/utils/date';
import { ProfileTypes, SignupIntents } from '@sda/enums';
import { getDisplayName } from '@sda/utils/string';
import {
  sessionUserSelector, venueSelectors, fingerprintSelector, hyperwalletAccountSelectors,
  sessionPositionSelector, sessionUserCookiesState, actSelectors, sessionGoogleClientIdSelector,
} from '..';
import { getSessionUserAdminStatus, getSessionUserState, getUserId } from './login';
import { getSelectedProfile } from './profile';
import type { NormalizedPublicProfile } from '@sda/interfaces/show';
import type { ExtendedUser } from '@sda/interfaces/reducer/session.reducer';

/**
 * Get the currently logged in user.
 * @param state
 * @return {*}
 */
export const getSessionUser = createSelector(
  sessionUserSelector,
  (user: ExtendedUser) => user,
);

/**
 * Gets the currently logged in user, but only returns primitives for better memoization
 */
export const getSessionUserFlat = createSelector(
  sessionUserSelector,
  (user: ExtendedUser) => {
    if (!user) {
      return null;
    }

    const flatUser = pickPrimitives(user);

    const result = {
      ...flatUser,
      createdAt: getDateTime(user.createdAt).toSeconds(),
      lastOnlineAt: getDateTime(user.lastOnlineAt).toSeconds(),
      updatedAt: getDateTime(user.updatedAt).toSeconds(),
    };

    return result;
  },
);

export const getSessionUserFullName = createSelector(
  getSessionUserFlat,
  (user) => {
    if (!user) {
      return '';
    }

    if (user.fullName) {
      return user.fullName;
    }

    let name = user.firstName;

    if (user.lastName) {
      name = `${name} ${user.lastName}`;
    }

    return name;
  },
);

/**
 * Fetches the email address of the logged-in user
 * @return {string}
 */
export const getSessionUserEmailAddress = createSelector(
  getSessionUserFlat,
  (user) => {
    if (!user) {
      return null;
    }

    return user.email;
  },
);

export const getFingerprint = createSelector(
  fingerprintSelector,
  (fingerprint) => fingerprint,
);

/**
 * Get the id of the current logged-in user, regardless of impersonation status.
 * @return {string}
 */
export const getCanonicalUserId = createSelector(
  getSessionUserFlat,
  (user) => {
    if (!user) {
      return null;
    }

    const { uid } = user;

    return uid;
  },
);

/**
 * Determine whether or not the current user is able to edit the given document and see its private data
 */
export const isOwnerOrCollaborator = createSelector(
  getSessionUserAdminStatus,
  getUserId,
  (state, document: { owner: string, collaborators: string[] }) => document,
  (isAdmin, userId: string, document: { owner: string, collaborators: string[] }) => {
    if (!document) {
      return false;
    }

    if (isAdmin) {
      return true;
    }

    return !!(userId && document && (document.owner === userId || (document.collaborators && document.collaborators.indexOf(userId) !== -1)));
  },
);

/**
 * Determine whether or not the current user is able to edit the given document and see its private data
 */
export const isOwnerOrCollaboratorOrModerator = createSelector(
  getSessionUserAdminStatus,
  getUserId,
  (state, document: { owner: string, collaborators: string[], moderators: string[] }) => document,
  (isAdmin, userId: string, document: { owner: string, collaborators: string[], moderators: string[] }) => {
    if (!document) {
      return false;
    }

    if (isAdmin) {
      return true;
    }

    return !!(
      userId
      && document
      && (
        document.owner === userId
        || (document.collaborators && document.collaborators.indexOf(userId) !== -1)
        || ((document.moderators && document.moderators.indexOf(userId) !== -1)
        )));
  },
);

/**
 * Determines if the current logged in user has Intercom available
 */
export const getSessionUserHasIntercom = createSelector(
  getSessionUserFlat,
  (user) => !user
  || (
    user
    && !user.isAdmin
    && (
      user.acceptsGDPR
      || user.hasIntercom
      || !!user.selectedProfileId
      || user.intent === SignupIntents.ARTIST
      || user.intent === SignupIntents.VENUE
    )),
);

/**
 * Fetches the current position associated with the user's session
 */
export const getCurrentPosition = createSelector(
  sessionPositionSelector,
  (position) => position || undefined,
);

/**
 * Get the users response to cookie policy
 */
export const getSessionUserCookiesState = createSelector(
  sessionUserCookiesState,
  (acceptsCookies) => acceptsCookies,
);

/**
 * Retrieves the path to redirect the user when landing on the root
 */
export const getSessionPathForUser = createSelector(
  getSessionUserState,
  getSessionUserFlat,
  (userState, user) => {
    if (userState === null) {
      return null;
    }

    if (!user) {
      return '/home';
    }

    const {
      isAdmin, selectedProfileId,
    } = user;

    if (userState) {
      if (isAdmin) {
        return '/connect';
      }

      if (!selectedProfileId) {
        return '/connect';
      }

      return '/connect';
    }

    return '/home';
  },
);

/**
 * Gets a list of venues collaborated on by the current user
 * @param  {any} state
 * @return {any[]|null}
 */
export const getUserVenues = createSelector(
  getUserId,
  venueSelectors.selectAll,
  (userId, items) => {
    if (!userId || isEmpty(items)) {
      return null;
    }

    return filter(items, (item) => item.collaborators && item.collaborators.indexOf(userId) !== -1);
  },
);

export const getUserHyperwalletAccounts = createSelector(
  hyperwalletAccountSelectors.selectAll,
  getUserId,
  (items, userId) => filter(items, (item) => item.owner && item.owner === userId),
);

/**
 * Gets a list of acts collaborated on by the current user
 * @param  {any} state
 * @return {any[]|null}
 */
export const getUserActs = createSelector(
  getUserId,
  actSelectors.selectAll,
  (userId, items) => {
    if (!userId || isEmpty(items)) {
      return null;
    }

    return filter(items, (item) => item.collaborators && item.collaborators.indexOf(userId) !== -1);
  },
);

export const getUserProfiles = createSelector(
  getUserActs,
  getUserVenues,
  (acts, venues): NormalizedPublicProfile[] => {
    const profiles = [
      ...map(acts, (act) => ({ ...act, profileType: ProfileTypes.ACT })),
      ...map(venues, (venue) => ({ ...venue, profileType: ProfileTypes.VENUE })),
    ];

    return uniqBy(orderBy(profiles, (item) => item.name), (item) => item.id);
  },
);

/**
 * Retrieves the number of profiles associated with the current user
 */
export const getUserProfileCount = createSelector(
  getUserActs,
  getUserVenues,
  (acts, venues) => [
    ...acts || [],
    ...venues || [],
  ].length,
);

/**
 * Fetches the current state of the selected profile having a connection associated with it
 */
export const getSessionUserHasConnection = createSelector(
  getSessionUserFlat,
  getUserProfileCount,
  (user, count) => user && (user.hasConnection || count > 1),
);

export const getGoogleAnalyticsClientId = createSelector(
  sessionGoogleClientIdSelector,
  (id) => id,
);

export const getSessionUserActivityName = createSelector(
  getSessionUserFlat,
  getSelectedProfile,
  (user, profile) => {
    if (user.isAdmin) {
      return 'Side Door';
    }

    if (profile) {
      return profile.name;
    }

    return getDisplayName(user);
  },
);

export * from './login';
export * from './profile';
